import ThermostatIcon from "@mui/icons-material/Thermostat"
import { Box, SxProps, Theme } from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  formatTemperature,
  formatTemperatureValue,
  LEVEL_COLORS,
  LEVEL_LABEL_KEYS,
  WARN_COLOR,
} from "./temperatureDemoFormat"
import {
  hadBreachInLast24h,
  temperatureAt,
  temperatureLevel,
  TemperatureLevel,
} from "./temperatureDemoGenerator"
import type { TemperatureDemoScenario } from "./temperatureDemoTypes"
import { useDemoNow } from "./useDemoNow"

/** Pill look per level — shared by the tile pill and the legend samples. */
export const temperaturePillSx = (level: TemperatureLevel) => {
  const c = LEVEL_COLORS[level]
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    height: 18,
    pl: "4px",
    pr: "6px",
    borderRadius: "9px",
    fontSize: "10.5px",
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    flexShrink: 0,
    bgcolor: c.pillBg,
    color: c.pillFg,
    border: c.pillBorder,
  }
}

/**
 * On a narrow tile the pill drops its icon and shows `12,4°` instead of `12,4 °C`.
 * The tile header is the size container (`OsLockerDoorV2`); 96px fits the
 * door circle plus the full pill.
 */
const compactSx = {
  "@container (max-width: 96px)": {
    "& .temperature-pill-icon, & .temperature-pill-full": { display: "none" },
    "& .temperature-pill-compact": { display: "inline" },
  },
} as const

const pulseSx = {
  "@keyframes temperatureDemoPulse": {
    "0%, 100%": { boxShadow: "0 0 0 0 rgba(179,38,30,.55)" },
    "50%": { boxShadow: "0 0 0 4px rgba(179,38,30,0)" },
  },
  animation: "temperatureDemoPulse 1.6s ease-in-out infinite",
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
} as const

type Props = {
  doorId: string
  scenario: TemperatureDemoScenario
}

/**
 * LOCKER-534 — the simulated reading on a door tile. The caller renders it
 * only for a door in the demo map of a demo-enabled locker.
 *
 * The visible text is `aria-hidden`; a visually-hidden sentence carries the
 * reading and its level for screen readers (colour alone is not a signal).
 */
export const DoorTemperaturePill: FC<Props> = ({ doorId, scenario }) => {
  const { t, i18n } = useTranslation()
  const now = useDemoNow()
  const { value, level, pastDot } = useMemo(() => {
    const v = temperatureAt(doorId, scenario, 0, now)
    const lv = temperatureLevel(v)
    return {
      value: v,
      level: lv,
      pastDot: lv === "ok" && hadBreachInLast24h(doorId, scenario, now),
    }
  }, [doorId, scenario, now])

  const text = formatTemperature(value, i18n.language)

  return (
    <Box
      component="span"
      data-testid={`temperature-pill-${doorId}`}
      data-level={level}
      title={pastDot ? t("temperatureDemo.pastBreachTitle") : undefined}
      sx={[temperaturePillSx(level), compactSx, level === "crit" && pulseSx] as SxProps<Theme>}
    >
      <ThermostatIcon className="temperature-pill-icon" aria-hidden sx={{ fontSize: 11 }} />
      <span className="temperature-pill-full" aria-hidden>{text}</span>
      <Box component="span" className="temperature-pill-compact" aria-hidden sx={{ display: "none" }}>
        {`${formatTemperatureValue(value, i18n.language)}°`}
      </Box>
      {pastDot && (
        <Box
          component="span"
          aria-hidden
          sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: WARN_COLOR, ml: "1px" }}
        />
      )}
      <Box component="span" sx={visuallyHidden}>
        {t("temperatureDemo.tileAria", {
          value: text,
          level: t(LEVEL_LABEL_KEYS[level]),
        })}
        {pastDot ? `, ${t("temperatureDemo.pastBreachTitle")}` : ""}
      </Box>
    </Box>
  )
}
